import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  MenuItem,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import deliveryAgentsApi from "../../api/delivery-agents";
import { format } from "date-fns";
import Layout from "../../components/layout/Layout";
import CreateDeliveryAgentDialog from "./CreateDeliveryAgentDialog";
import EditDeliveryAgentDialog from "./EditDeliveryAgentDialog";
import DeliveryAgentDetailsDialog from "./DeliveryAgentDetailsDialog";

interface DeliveryAgent {
  _id: string;
  name: string;
  email: string;
  phoneNumber: string;
  isActive: boolean;
  createdAt: string;
  subscriptionsCount: number;
  ordersCount: number;
  aadharNumber?: string;
  bikeNumber?: string;
  rating?: number;
  noOfRatings?: number;
  agentType?: string;
  instantStatus?: string;
  activeInstantOrdersCount?: number;
  todayInstantOrdersCount?: number;
  totalInstantOrdersCount?: number;
  isOnline?: boolean;
  lastSeen?: string;
  isBusyWithSubscription?: boolean;
}

const DeliveryAgents: React.FC = () => {
  const [deliveryAgents, setDeliveryAgents] = useState<DeliveryAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedDeliveryAgent, setSelectedDeliveryAgent] = useState<DeliveryAgent | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [detailsAgentId, setDetailsAgentId] = useState<string | null>(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const [isActive, setIsActive] = useState<boolean | null>(null);

  // Handle search input with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchDeliveryAgents();
  }, [page, rowsPerPage, debouncedSearch, isActive]);

  const fetchDeliveryAgents = async () => {
    setLoading(true);

    try {
      const data = await deliveryAgentsApi.getDeliveryAgents({
        search: debouncedSearch,
        isActive: isActive === null ? undefined : isActive,
        page: page + 1,
        limit: rowsPerPage,
      });

      setDeliveryAgents(data.deliveryAgents);
      setTotal(data.pagination.total);
    } catch (err: any) {
      setError(err.response?.data?.message || "Error fetching delivery agents");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleResetFilters = () => {
    setSearchTerm("");
    setDebouncedSearch("");
    setIsActive(null);
  };

  const handleCreateSuccess = () => {
    fetchDeliveryAgents();
  };

  const handleEditSuccess = () => {
    fetchDeliveryAgents();
  };

  const handleEdit = (deliveryAgent: DeliveryAgent) => {
    setSelectedDeliveryAgent(deliveryAgent);
    setEditDialogOpen(true);
  };

  const handleDeactivate = async (deliveryAgentId: string) => {
    try {
      await deliveryAgentsApi.updateDeliveryAgentStatus(deliveryAgentId, false);
      fetchDeliveryAgents();
    } catch (error) {
      console.error("Error deactivating delivery agent:", error);
    }
  };

  const handleActivate = async (deliveryAgentId: string) => {
    try {
      await deliveryAgentsApi.updateDeliveryAgentStatus(deliveryAgentId, true);
      fetchDeliveryAgents();
    } catch (error) {
      console.error("Error activating delivery agent:", error);
    }
  };

  return (
    <Layout>
      <Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h4">Delivery Agents</Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Add Delivery Agent
          </Button>
        </Box>

        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            <Box sx={{ flex: "1 1 300px", minWidth: "200px" }}>
              <TextField
                label="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                fullWidth
                size="small"
                placeholder="Search by name, email or phone"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton edge="end">
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box sx={{ flex: "1 1 200px", minWidth: "150px" }}>
              <TextField
                select
                label="Status"
                value={
                  isActive === null ? "" : isActive ? "active" : "inactive"
                }
                onChange={(e) => {
                  if (e.target.value === "") {
                    setIsActive(null);
                  } else {
                    setIsActive(e.target.value === "active");
                  }
                }}
                fullWidth
                size="small"
              >
                <MenuItem value="">All Users</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </TextField>
            </Box>
            <Box
              sx={{
                flex: "1 1 200px",
                minWidth: "150px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Button variant="outlined" onClick={handleResetFilters} fullWidth>
                Reset Filters
              </Button>
            </Box>
          </Box>
        </Paper>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name / Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Duty Status</TableCell>
                <TableCell>Active Load</TableCell>
                <TableCell>Instant Delivs (Today/Total)</TableCell>
                <TableCell>Subscr Orders</TableCell>
                <TableCell>Rating</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    Error: {error}
                  </TableCell>
                </TableRow>
              ) : deliveryAgents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    No delivery agents found
                  </TableCell>
                </TableRow>
              ) : (
                deliveryAgents.map((deliveryAgent) => {
                  const hasInstant = deliveryAgent.agentType === "instant" || deliveryAgent.agentType === "both";
                  const dutyStatus = deliveryAgent.instantStatus || "OFF_DUTY";
                  const isLivenessActive = deliveryAgent.lastSeen
                    ? new Date().getTime() - new Date(deliveryAgent.lastSeen).getTime() < 5 * 60 * 1000
                    : false;

                  const statusLabel =
                    dutyStatus === "ON_DELIVERY"
                      ? "ON DELIVERY"
                      : deliveryAgent.isBusyWithSubscription
                      ? "ON SUBSCRIPTION"
                      : dutyStatus === "AVAILABLE" && isLivenessActive
                      ? "AVAILABLE"
                      : dutyStatus === "AVAILABLE" && !isLivenessActive
                      ? "UNREACHABLE"
                      : "OFF DUTY";

                  const dotColor =
                    statusLabel === "AVAILABLE"
                      ? "#22c55e"
                      : statusLabel === "ON DELIVERY" || statusLabel === "ON SUBSCRIPTION"
                      ? "#f59e0b"
                      : statusLabel === "UNREACHABLE"
                      ? "#ef4444"
                      : "#64748b";

                  return (
                    <TableRow key={deliveryAgent._id} sx={{ "&:hover": { backgroundColor: "#f8fafc" } }}>
                      <TableCell>
                        <Typography fontWeight={700}>{deliveryAgent.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{deliveryAgent.email || "—"}</Typography>
                      </TableCell>
                      <TableCell>{deliveryAgent.phoneNumber}</TableCell>
                      <TableCell>
                        <Chip
                          label={deliveryAgent.agentType ? deliveryAgent.agentType.toUpperCase() : "SUBSCRIPTION"}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: "0.675rem", fontWeight: 800 }}
                        />
                      </TableCell>
                      <TableCell>
                        {hasInstant ? (
                          <Box display="flex" alignItems="center" gap={0.75}>
                            <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: dotColor }} />
                            <Typography variant="body2" fontWeight={600}>
                              {statusLabel}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        {hasInstant ? (deliveryAgent.activeInstantOrdersCount || 0) : "—"}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {hasInstant ? `${deliveryAgent.todayInstantOrdersCount || 0} / ${deliveryAgent.totalInstantOrdersCount || 0}` : "—"}
                      </TableCell>
                      <TableCell>{deliveryAgent.ordersCount || 0}</TableCell>
                      <TableCell>
                        {deliveryAgent.rating !== undefined && deliveryAgent.noOfRatings && deliveryAgent.noOfRatings > 0
                          ? `${deliveryAgent.rating.toFixed(1)} ★ (${deliveryAgent.noOfRatings})`
                          : "—"}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              setDetailsAgentId(deliveryAgent._id);
                              setDetailsDialogOpen(true);
                            }}
                          >
                            Live Status
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleEdit(deliveryAgent)}
                          >
                            Edit
                          </Button>
                          {deliveryAgent.isActive ? (
                            <Button
                              size="small"
                              onClick={() => handleDeactivate(deliveryAgent._id)}
                              variant="outlined"
                              color="warning"
                            >
                              Deactivate
                            </Button>
                          ) : (
                            <Button
                              size="small"
                              variant="outlined"
                              color="success"
                              onClick={() => handleActivate(deliveryAgent._id)}
                            >
                              Activate
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      </Box>

      <CreateDeliveryAgentDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={handleCreateSuccess}
      />

      <EditDeliveryAgentDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedDeliveryAgent(null);
        }}
        onSuccess={handleEditSuccess}
        deliveryAgent={selectedDeliveryAgent}
      />

      <DeliveryAgentDetailsDialog
        open={detailsDialogOpen}
        onClose={() => {
          setDetailsDialogOpen(false);
          setDetailsAgentId(null);
        }}
        deliveryAgentId={detailsAgentId}
      />
    </Layout>
  );
};

export default DeliveryAgents;
